import {
  EventName,
  SectionData,
  SectionType,
  Website,
  WebsitePreviewEvent,
  WebsitePreviewEventGenerateSuggestions,
} from '@betterfunnel/types';
import {
  CheckCircleIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
} from '@heroicons/react/24/outline';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
} from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import AlertModal from '../components/Common/AlertModal';
import Badge from '../components/Common/Badge';
import Button from '../components/Common/Button';
import ButtonGroup from '../components/Common/ButtonGroup';
import FullScreenModal from '../components/Common/FullScreenModal';
import Loader from '../components/Common/Loader';
import Select from '../components/Common/Select';
import Toggle from '../components/Common/Toggle';
import EditorPopup from '../components/EditorPopup';
import { EditStructuredSectionItemModal } from '../components/SectionStructureForm/EditStructuredSectionItemModal';
import EditorSidebar from '../components/WebsiteEditor/EditorSidebar';
import { WebsitePreview } from '../components/WebsitePreview';
import { useAISuggestionsContext } from '../contexts/AISuggestionsContextProvider';
import { AuthContext } from '../contexts/AuthContext';
import { useDashboardContext } from '../contexts/DashboardContext';
import { UserContext } from '../contexts/UserContext';
import {
  WebsiteUpdate,
  WebsiteUpdateType,
  WebsiteUpdaterContext,
} from '../contexts/WebsiteUpdaterContext';
import { functions } from '../Firebase';
import {
  deleteFavoriteSection,
  removeSectionVariantFromCatalog,
} from '../services/SectionsService';
import {
  PREVIEW_ROOT,
  classNames,
  emptyConverter,
  sendMessageToIframe,
} from '../utils';
import { track } from '../utils/analytics';
import { applyExtraData } from '../utils/website';
import AddFavoriteSectionModal from './AddFavoriteSectionModal';
import AddSectionItemModal from './AddSectionItemModal';
import { AddSectionApprovalModal } from './AddSectionModal';
import AddSectionToCatalogModal from './AddSectionToCatalogModal';
import EditQuillSearchParamMentionModal from './EditQuillSearchParamMentionModal';
import ImageLibraryModal from './ImageLibraryModal/ImageLibraryModal';

interface Props {
  open: boolean;
  onClose: () => void;
  websiteId: string;
}

export default function EditorModal({ open, onClose, websiteId }: Props) {
  const [device, setDevice] = useState('desktop');
  const { isSupportAdmin } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const {
    selectedDynamicPage,
    accountBrand,
    accountConfig,
    forms,
    getSectionListItemFieldConfig,
  } = useDashboardContext();
  const {
    saveStatus,
    setSaveStatus,
    publishing,
    publishWebsite,
    discarding,
    discardChanges,
    createWebsiteUpdate,
    updateWebsite,
    canRevert,
    canForward,
    revertLatestUpdate,
    forwardRevert,
    applyChangesSync,
    clearHistory,
  } = useContext(WebsiteUpdaterContext);
  const { fieldSuggestions } = useAISuggestionsContext();

  const [website, setWebsite] = useState<Website>();
  const [activeWebsiteUpdate, setActiveWebsiteUpdate] =
    useState<WebsiteUpdate>();
  const [addSectionModalOpen, setAddSectionModalOpen] = useState(false);
  const [addFavoriteSectionModalOpen, setAddFavoriteSectionModalOpen] =
    useState<Partial<SectionData>>();
  const [addSectionToCatalogModalOpen, setAddSectionToCatalogModalOpen] =
    useState<SectionData>();
  const [removeFavoriteSectionModalOpen, setRemoveFavoriteSectionModalOpen] =
    useState<{ favoriteSectionId: string; loading?: boolean } | undefined>();
  const [
    removeSectionFromCatalogModalOpen,
    setRemoveSectionFromCatalogModalOpen,
  ] = useState<
    | { sectionTypeConfigId: string; variantId: string; loading?: boolean }
    | undefined
  >();
  const [sectionItemEditModalOpen, setSectionItemEditModalOpen] =
    useState<SectionType>();
  const [selectImageModalOpen, setSelectImageModalOpen] = useState(false);
  const [deleteSectionModalOpen, setDeleteSectionModalOpen] = useState(false);
  const [addSectionItemModalOpen, setAddSectionItemModalOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [discardChangesConfirmModalOpen, setDiscardChangesConfirmModalOpen] =
    useState(false);
  const [
    editQuillSearchParamMentionModalOpen,
    setEditQuillSearchParamMentionModalOpen,
  ] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [
    beforeCloseEditorWithoutSaveModalOpen,
    setBeforeCloseEditorWithoutSaveModalOpen,
  ] = useState(false);

  const id = useMemo(() => uid(8), []);
  const unpublishedChanges = useMemo(
    () => (website ? website?.changesToPublish !== false : false),
    [website],
  );

  // Reset
  useEffect(() => {
    setReady(false);
    setSaveStatus('none');
    clearHistory();
  }, [clearHistory, open, setSaveStatus]);

  // Listener for browser/tab close
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (unpublishedChanges) {
        event.preventDefault();
        event.returnValue = ''; // Required for most browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [unpublishedChanges]);

  const updateWebsiteByEvent = useCallback(
    (wu: WebsiteUpdate) => {
      updateWebsite(wu);

      if (website) {
        sendMessageToIframe({
          eventName: 'websiteUpdated',
          website: applyChangesSync(websiteId, website.development, [wu]),
        });
      }
    },
    [applyChangesSync, updateWebsite, website, websiteId],
  );

  const handleGetDataEvent = useCallback(
    (data: WebsitePreviewEvent) => {
      const getSectionVariants = httpsCallable<any, any>(
        functions,
        'accounts-websites-getSectionVariants',
      );

      getSectionVariants({
        accountId: selectedAccount?.id,
        campaignId: selectedDynamicPage?.id,
        websiteId: websiteId,
      }).then((result) => {
        sendMessageToIframe({
          requestId: (data as any).requestId,
          eventName: 'sendData',
          sendDataType: 'sectionVariants',
          section: {},
          sectionTypes: result.data.sectionTypes,
          favoriteSections: result.data.favoriteSections,
          isSystemAdmin: isSupportAdmin,
        });
      });
    },
    [selectedAccount?.id, selectedDynamicPage?.id, websiteId, isSupportAdmin],
  );

  const handleGenerateSuggestionsEvent = useCallback(
    (data: WebsitePreviewEventGenerateSuggestions) => {
      fieldSuggestions(
        websiteId,
        data.data.sectionId,
        data.data.propKey,
        data.data.fullLine,
        data.data.textSelection,
        data.data.prompt,
      )
        .then((result) => {
          sendMessageToIframe({
            eventName: 'returnGenerateSuggestions',
            requestId: data.data.requestId,
            suggestions: result,
          });
        })
        .catch((e) => {
          sendMessageToIframe({
            eventName: 'returnGenerateSuggestions',
            requestId: data.data.requestId,
            error: e,
          });
        });
    },
    [fieldSuggestions, websiteId],
  );

  const handleWebsiteUpdateEvent = useCallback(
    (wu: WebsiteUpdate) => {
      if (wu?.type === 'addSectionBelow') {
        setActiveWebsiteUpdate(wu);
        setAddSectionModalOpen(true);
        return;
      }
      if (wu?.type === 'selectImage') {
        setActiveWebsiteUpdate(wu);
        setSelectImageModalOpen(true);
        return;
      }
      if (wu?.type === 'deleteSection') {
        setActiveWebsiteUpdate(wu);
        setDeleteSectionModalOpen(true);
        return;
      }
      if (wu?.type === 'addSectionItem') {
        const sectionType = website?.development.sections.find(
          (i) => i.id === wu?.sectionId,
        )?.type;
        if (
          sectionType === 'comparisonList' ||
          sectionType === 'comparisonTopItems'
        ) {
          setActiveWebsiteUpdate(wu);
          setAddSectionItemModalOpen(true);
          return;
        }
      }
      if (wu?.type === 'editSectionItem') {
        const sectionType = website?.development.sections.find(
          (i) => i.id === wu?.sectionId,
        )?.type;
        setActiveWebsiteUpdate(wu);
        setSectionItemEditModalOpen(sectionType);
        return;
      }
      if (wu?.type === 'editSection') {
        setActiveWebsiteUpdate(wu);
        setSidebarOpen(true);
        return;
      }
      if (wu?.type === 'customValueMentionClick') {
        setActiveWebsiteUpdate(wu);
        setEditQuillSearchParamMentionModalOpen(true);
        return;
      }

      updateWebsiteByEvent(wu);
    },
    [updateWebsiteByEvent, website?.development.sections],
  );

  const handlePreviewEvent = useCallback(
    (event: MessageEvent<string>) => {
      if (event.origin !== PREVIEW_ROOT) return;
      if (!selectedDynamicPage) return;
      const iframe = (document.getElementById(id) as HTMLIFrameElement)
        ?.contentWindow;
      if (event.source !== iframe) return;

      try {
        const data: WebsitePreviewEvent = JSON.parse(event.data);
        console.log('[WEBSITE_UPDATER] event', data?.eventName, data);

        if (data.eventName === 'ready') {
          console.warn(
            '[WEBSITE_PREVIEW_EVENT] ready event recieved',
            data.eventName,
            !!website,
          );
          setReady(true);
          return;
        }

        if (data.eventName === 'getData') {
          handleGetDataEvent(data);
          return;
        }

        if (data.eventName === 'generateSuggestions') {
          handleGenerateSuggestionsEvent(
            data as WebsitePreviewEventGenerateSuggestions,
          );
          return;
        }

        if (data.eventName === 'addToFavorites') {
          setAddFavoriteSectionModalOpen(data.data.section);
          return;
        }

        if (data.eventName === 'removeFromFavorites') {
          setRemoveFavoriteSectionModalOpen({
            favoriteSectionId: data.data.favoriteSectionId,
          });
          return;
        }

        if (data.eventName === 'addToCatalog') {
          setAddSectionToCatalogModalOpen(data.data.section);
          return;
        }

        if (data.eventName === 'removeFromCatalog') {
          setRemoveSectionFromCatalogModalOpen({
            sectionTypeConfigId: data.data.sectionTypeConfigId,
            variantId: data.data.variantId,
          });
          return;
        }

        const eventToChangeMap: {
          [key in EventName]: WebsiteUpdateType | undefined;
        } = {
          getData: undefined,
          generateSuggestions: undefined,
          addToFavorites: undefined,
          removeFromFavorites: undefined,
          ready: undefined,
          addToCatalog: undefined,
          removeFromCatalog: undefined,

          changeDesign: 'changeDesign',
          addSectionBelow: 'addSectionBelow',
          textChanged: 'text',
          moveSectionDown: 'moveSectionDown',
          moveSectionUp: 'moveSectionUp',
          addSectionItem: 'addSectionItem',
          deleteSection: 'deleteSection',
          deleteSectionItem: 'deleteSectionItem',
          editSectionItem: 'editSectionItem',
          duplicateSectionItem: 'duplicateSectionItem',
          duplicateSection: 'duplicateSection',
          selectImage: 'selectImage',
          editSection: 'editSection',
          editImageSettings: 'editImageSettings',
          customValueMentionClick: 'customValueMentionClick',
        };
        const changeType = eventToChangeMap[data.eventName];

        if (!changeType) {
          console.warn('[WEBSITE_PREVIEW_EVENT] unknown event', data.eventName);
          return;
        }

        const wu = createWebsiteUpdate(
          selectedDynamicPage.id,
          websiteId,
          data.data.sectionId,
          data.data.propKey,
          data.data.content,
          changeType,
          data.data.subitems,
          website?.development
            ? JSON.parse(JSON.stringify(website?.development))
            : undefined,
        );

        if (!wu) {
          console.warn(
            '[WEBSITE_PREVIEW_EVENT] unable to create website update object',
            wu,
          );
          return;
        }

        handleWebsiteUpdateEvent(wu);
      } catch (e) {
        Sentry.captureException(e);
        console.warn('[WEBSITE_PREVIEW_EVENT] failed to parse event data', e);
      }
    },
    [
      selectedDynamicPage,
      id,
      createWebsiteUpdate,
      websiteId,
      website,
      handleWebsiteUpdateEvent,
      handleGetDataEvent,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', handlePreviewEvent);

    return () => {
      window.removeEventListener('message', handlePreviewEvent);
    };
  }, [handlePreviewEvent]);

  useEffect(() => {
    if (!selectedDynamicPage || !websiteId || !ready) return;

    const websiteRef = doc(
      selectedDynamicPage.ref,
      'websites',
      websiteId,
    ).withConverter(emptyConverter<Website>());
    const unsubscribe = onSnapshot(websiteRef, (website) => {
      if (!website.exists()) return;

      const enrichedWebsite = applyExtraData(
        website.data(),
        selectedDynamicPage.data(),
        accountBrand,
        accountConfig,
      );

      enrichedWebsite.development.formIds =
        forms?.map((f) => f.id) || enrichedWebsite.development.formIds;

      enrichedWebsite.development.forms = forms
        ? Object.fromEntries(forms?.map((f) => [f.id, f.data()]))
        : enrichedWebsite.development.forms;

      setWebsite(enrichedWebsite);

      sendMessageToIframe({
        eventName: 'websiteUpdated',
        website: enrichedWebsite.development,
      });
    });

    return () => {
      unsubscribe();
    };
  }, [
    websiteId,
    selectedDynamicPage,
    accountBrand,
    accountConfig,
    ready,
    forms,
  ]);

  const onOpenSidebar = () => {
    setSidebarCollapsed(true);
  };

  const onCloseSidebar = () => {
    setSidebarCollapsed(false);
  };

  const onCloseEditor = () => {
    if (unpublishedChanges) {
      setBeforeCloseEditorWithoutSaveModalOpen(true);
      return;
    }
    onClose();
  };

  useEffect(() => {
    setSidebarCollapsed(previewMode);
  }, [previewMode]);

  return (
    <FullScreenModal open={open} onClose={() => {}}>
      <div className="flex h-full flex-row items-start justify-start gap-4 px-4 pb-4 pt-5 sm:p-6">
        <EditorSidebar
          collapsed={sidebarCollapsed}
          website={website}
          onCloseEditor={onCloseEditor}
          onOpen={onOpenSidebar}
          onClose={onCloseSidebar}
          websiteId={websiteId}
          onUpdateWebsiteEvent={(wu: WebsiteUpdate) => {
            handleWebsiteUpdateEvent(wu);
          }}
        />
        <div className="flex h-full w-full flex-col">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Select
                selected={device}
                onChange={(v) => setDevice(v)}
                options={[
                  {
                    value: 'desktop',
                    child: (
                      <div className="inline-flex items-center gap-x-1.5 px-2">
                        <ComputerDesktopIcon
                          className="-ml-0.5 h-4 w-4"
                          aria-hidden="true"
                        />
                        Desktop
                      </div>
                    ),
                  },
                  {
                    value: 'mobile',
                    child: (
                      <div className="inline-flex items-center gap-x-1.5 px-2">
                        <DevicePhoneMobileIcon
                          className="-ml-0.5 h-4 w-4"
                          aria-hidden="true"
                        />
                        Mobile
                      </div>
                    ),
                  },
                  {
                    value: 'tablet',
                    child: (
                      <div className="inline-flex items-center gap-x-1.5 px-2">
                        <DeviceTabletIcon
                          className="-ml-0.5 h-4 w-4"
                          aria-hidden="true"
                        />
                        Tablet
                      </div>
                    ),
                  },
                ]}
              />
              <div className="ms-3 flex flex-col content-center items-baseline gap-1 text-xs">
                <span>Preview</span>
                <Toggle
                  size="small"
                  checked={previewMode}
                  onChange={(value: boolean) => {
                    setPreviewMode(value);
                  }}
                />
              </div>
              {saveStatus === 'saving' ? (
                <div className="ms-3 flex flex-row items-baseline gap-2 text-xs">
                  <Loader size={4} />
                  <span>Saving...</span>
                </div>
              ) : (
                <></>
              )}
              {saveStatus === 'done' ? (
                <div className="ms-3 flex flex-row items-center gap-2 text-xs">
                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                  <span>Saved</span>
                </div>
              ) : (
                <></>
              )}
            </div>
            {unpublishedChanges ? (
              <div className="flex items-center justify-center">
                <Badge title="Unsaved Modifications" color="yellow" />
              </div>
            ) : (
              <></>
            )}
            <div className="flex items-stretch gap-4">
              <ButtonGroup
                items={[
                  {
                    icon: ArrowUturnLeftIcon,
                    onClick: revertLatestUpdate,
                    disabled: !canRevert,
                  },
                  {
                    icon: ArrowUturnRightIcon,
                    onClick: forwardRevert,
                    disabled: !canForward,
                  },
                ]}
              />
              {unpublishedChanges ? (
                <Button
                  size="large"
                  variant="secondary"
                  // color="negative"
                  loading={discarding}
                  onClick={() => {
                    track('discard_changes_button_click', {
                      dynamicPageId: selectedDynamicPage?.id,
                      websiteId,
                    });
                    setDiscardChangesConfirmModalOpen(true);
                  }}
                >
                  Discard
                </Button>
              ) : (
                <></>
              )}
              <Button
                size="large"
                disabled={!unpublishedChanges || saveStatus === 'saving'}
                loading={publishing}
                onClick={() => {
                  track('publish_website_button_click', {
                    dynamicPageId: selectedDynamicPage?.id,
                    websiteId,
                  });
                  publishWebsite(selectedDynamicPage?.id ?? '', websiteId);
                }}
              >
                Save
              </Button>
              {/* <Button
                variant="secondary"
                size="large"
                icon={
                  <QuestionMarkCircleIcon
                    className="-ml-0.5 h-4 w-4"
                    aria-hidden="true"
                  />
                }
                onClick={() => {
                  window.open('https://docs.betterfunnel.ai', '_blank');
                }}
              ></Button> */}
              {/* <Button
                variant="secondary"
                size="large"
                icon={
                  <ArrowTopRightOnSquareIcon
                    className="-ml-0.5 h-4 w-4"
                    aria-hidden="true"
                  />
                }
                onClick={() =>
                  window.open(
                    `https://${primaryDomain?.data().domain}/${
                      selectedDynamicPage?.data().path
                    }?utm=test_${websiteId}`,
                  )
                }
              ></Button> */}
            </div>
          </div>
          <div
            className={classNames(
              'relative mx-auto rounded-[2.5rem] border-gray-800 bg-gray-800 transition-all',
              device === 'desktop' ? 'mt-4 h-full w-full' : '',
              device === 'mobile'
                ? 'mt-8 h-[780px] w-[390px] border-[14px]'
                : '',
              device === 'tablet'
                ? 'mt-8 h-[454px] w-full max-w-[341px] border-[14px] md:h-[682px] md:max-w-[512px]'
                : '',
            )}
          >
            {device !== 'desktop' ? (
              <>
                <div className="absolute -left-[17px] top-[72px] h-[32px] w-[3px] rounded-l-lg bg-gray-800"></div>
                <div className="absolute -left-[17px] top-[124px] h-[46px] w-[3px] rounded-l-lg bg-gray-800"></div>
                <div className="absolute -left-[17px] top-[178px] h-[46px] w-[3px] rounded-l-lg bg-gray-800"></div>
                <div className="absolute -right-[17px] top-[142px] h-[64px] w-[3px] rounded-r-lg bg-gray-800"></div>
              </>
            ) : (
              <></>
            )}
            <div
              className={classNames(
                'h-full w-full overflow-hidden bg-white',
                device !== 'desktop' ? 'rounded-[2rem]' : '',
              )}
              style={{ boxShadow: '0 0 25px rgba(0,0,0,.11)' }}
            >
              <WebsitePreview
                websiteId={websiteId}
                editable={!previewMode}
                id={id}
              />
            </div>

            <EditorPopup
              open={sidebarOpen}
              section={
                sidebarOpen
                  ? (website?.development.sections.find(
                      (i) => i.id === activeWebsiteUpdate?.sectionId,
                    ) ??
                    website?.development.banners?.find(
                      (i) => i.id === activeWebsiteUpdate?.sectionId,
                    ))
                  : undefined
              }
              onClose={() => setSidebarOpen(false)}
              onChange={(section) => {
                if (!selectedDynamicPage) return;

                const wu = createWebsiteUpdate(
                  selectedDynamicPage.id,
                  websiteId,
                  section.id,
                  '',
                  section,
                  'editSection',
                  [],
                  website?.development
                    ? JSON.parse(JSON.stringify(website?.development))
                    : undefined,
                );

                if (!wu) return;

                updateWebsiteByEvent(wu);
              }}
            />
          </div>
        </div>
        <AddSectionApprovalModal
          websiteId={websiteId}
          open={addSectionModalOpen}
          section={activeWebsiteUpdate?.content}
          onClose={() => setAddSectionModalOpen(false)}
          onAdd={(section) => {
            setAddSectionModalOpen(false);
            const wu = activeWebsiteUpdate;
            if (!wu) return;

            updateWebsiteByEvent({ ...wu, content: section });
          }}
        />
        <AddFavoriteSectionModal
          open={!!addFavoriteSectionModalOpen}
          section={addFavoriteSectionModalOpen}
          onClose={() => setAddFavoriteSectionModalOpen(undefined)}
        />
        <AddSectionToCatalogModal
          open={!!addSectionToCatalogModalOpen}
          section={addSectionToCatalogModalOpen}
          onClose={() => setAddSectionToCatalogModalOpen(undefined)}
        />
        <AddSectionItemModal
          section={
            addSectionItemModalOpen
              ? website?.development.sections.find(
                  (i) => i.id === activeWebsiteUpdate?.sectionId,
                )
              : undefined
          }
          open={addSectionItemModalOpen}
          onClose={() => setAddSectionItemModalOpen(false)}
          onAdd={(item) => {
            setAddSectionItemModalOpen(false);
            const wu = activeWebsiteUpdate;
            if (!wu) return;

            updateWebsiteByEvent({ ...wu, content: item });
          }}
        />
        <ImageLibraryModal
          open={selectImageModalOpen}
          onClose={() => setSelectImageModalOpen(false)}
          onSelected={(image) => {
            setSelectImageModalOpen(false);
            const wu = activeWebsiteUpdate;
            if (!wu) return;
            console.log('image[0]', image[0]);
            updateWebsiteByEvent({ ...wu, content: image[0] });
          }}
          types={['image', 'video']}
        />
        <EditStructuredSectionItemModal
          open={!!sectionItemEditModalOpen}
          onClose={() => setSectionItemEditModalOpen(undefined)}
          field={getSectionListItemFieldConfig(
            sectionItemEditModalOpen ?? '',
            activeWebsiteUpdate?.propKey ?? '',
          )}
          item={(
            website?.development.sections?.find(
              (s) => s.id === activeWebsiteUpdate?.sectionId,
            ) as any
          )?.[activeWebsiteUpdate?.propKey ?? '']?.find?.(
            (i: any) => i.id === activeWebsiteUpdate?.content?.id,
          )}
          onSave={(data) => {
            setSectionItemEditModalOpen(undefined);
            const wu = activeWebsiteUpdate;
            if (!wu) return;

            updateWebsiteByEvent({
              ...wu,
              content: { id: wu.content.id, data },
            });
          }}
        />
        <AlertModal
          open={beforeCloseEditorWithoutSaveModalOpen}
          onClose={() => setBeforeCloseEditorWithoutSaveModalOpen(false)}
          title="Are you sure you want to leave without saving?"
          description="If you close the editor without clicking 'Save,' the changes will remain in draft and will not be published to the live site."
          actions={[
            {
              title: 'Cancel',
              onClick: () => setBeforeCloseEditorWithoutSaveModalOpen(false),
              type: 'secondary',
            },
            {
              title: 'Close without saving',
              type: 'primary',
              color: 'primary',
              onClick: () => {
                setBeforeCloseEditorWithoutSaveModalOpen(false);
                onClose();
              },
            },
          ]}
          type={'warn'}
        />
        <AlertModal
          open={deleteSectionModalOpen}
          onClose={() => setDeleteSectionModalOpen(false)}
          title="Are you sure you want to delete this section?"
          description="This action is not reversible."
          actions={[
            {
              title: 'Cancel',
              onClick: () => setDeleteSectionModalOpen(false),
              type: 'secondary',
            },
            {
              title: 'Delete',
              type: 'primary',
              color: 'negative',
              onClick: () => {
                setDeleteSectionModalOpen(false);
                const wu = activeWebsiteUpdate;
                if (!wu) return;

                updateWebsiteByEvent({ ...wu });
              },
            },
          ]}
          type={'warn'}
        />
        <AlertModal
          open={!!removeFavoriteSectionModalOpen}
          onClose={() => setRemoveFavoriteSectionModalOpen(undefined)}
          title="Are you sure you want to delete this section from the favorites?"
          description="This action is not reversible."
          actions={[
            {
              title: 'Cancel',
              onClick: () => setRemoveFavoriteSectionModalOpen(undefined),
              type: 'secondary',
            },
            {
              title: 'Delete',
              type: 'primary',
              color: 'negative',
              loading: removeFavoriteSectionModalOpen?.loading,
              onClick: () => {
                const favoriteSectionId =
                  removeFavoriteSectionModalOpen?.favoriteSectionId;

                if (!selectedAccount || !favoriteSectionId) {
                  setRemoveFavoriteSectionModalOpen(undefined);
                  return;
                }

                setRemoveFavoriteSectionModalOpen({
                  favoriteSectionId,
                  loading: true,
                });

                deleteFavoriteSection(
                  selectedAccount?.id ?? '',
                  favoriteSectionId,
                )
                  .then(() =>
                    handleGetDataEvent({ eventName: 'getData', data: {} }),
                  )
                  .then(() => setRemoveFavoriteSectionModalOpen(undefined))
                  .catch((e) => {
                    Sentry.captureException(e);
                    setRemoveFavoriteSectionModalOpen({
                      favoriteSectionId,
                      loading: false,
                    });
                  });
              },
            },
          ]}
          type={'warn'}
        />
        <AlertModal
          open={!!removeSectionFromCatalogModalOpen}
          onClose={() => setRemoveSectionFromCatalogModalOpen(undefined)}
          title="Are you sure you want to delete this section from the catalog?"
          description="This action is not reversible."
          actions={[
            {
              title: 'Cancel',
              onClick: () => setRemoveSectionFromCatalogModalOpen(undefined),
              type: 'secondary',
            },
            {
              title: 'Delete',
              type: 'primary',
              color: 'negative',
              loading: removeSectionFromCatalogModalOpen?.loading,
              onClick: () => {
                const sectionTypeConfigId =
                  removeSectionFromCatalogModalOpen?.sectionTypeConfigId;
                const variantId = removeSectionFromCatalogModalOpen?.variantId;

                if (!selectedAccount || !sectionTypeConfigId || !variantId) {
                  setRemoveSectionFromCatalogModalOpen(undefined);
                  return;
                }

                setRemoveSectionFromCatalogModalOpen({
                  ...removeSectionFromCatalogModalOpen,
                  loading: true,
                });

                removeSectionVariantFromCatalog(sectionTypeConfigId, variantId)
                  .then(() =>
                    handleGetDataEvent({ eventName: 'getData', data: {} }),
                  )
                  .then(() => setRemoveSectionFromCatalogModalOpen(undefined))
                  .catch((e) => {
                    Sentry.captureException(e);
                    setRemoveSectionFromCatalogModalOpen({
                      ...removeSectionFromCatalogModalOpen,
                      loading: false,
                    });
                  });
              },
            },
          ]}
          type={'warn'}
        />
        {unpublishedChanges ? (
          <AlertModal
            open={discardChangesConfirmModalOpen}
            onClose={() => setDiscardChangesConfirmModalOpen(false)}
            title="Are you sure you want to discard all the unpublished changes?"
            description="This action is not reversible."
            actions={[
              {
                title: 'Cancel',
                onClick: () => setDiscardChangesConfirmModalOpen(false),
                type: 'secondary',
              },
              {
                title: 'Discard',
                type: 'primary',
                color: 'negative',
                onClick: () => {
                  setDiscardChangesConfirmModalOpen(false);
                  if (!selectedDynamicPage) return;

                  discardChanges(selectedDynamicPage?.id, websiteId);
                },
              },
            ]}
            type={'warn'}
          />
        ) : (
          <></>
        )}
        <EditQuillSearchParamMentionModal
          mention={activeWebsiteUpdate?.content?.mention}
          open={editQuillSearchParamMentionModalOpen}
          onClose={() => {
            setEditQuillSearchParamMentionModalOpen(false);
          }}
          onSave={(newMention) => {
            const wu = activeWebsiteUpdate;
            if (!wu) return;

            const content = wu.content?.text;
            if (!content || typeof content !== 'object' || !content.ops) return;

            const newContentOps = content.ops.map((op: any) => {
              const insert = op.insert as any;
              const currentMention = insert?.['custom-value-mention'];
              if (currentMention && currentMention.id === newMention.id) {
                return {
                  ...op,
                  insert: {
                    ...insert,
                    'custom-value-mention': newMention,
                  },
                };
              }

              return op;
            });

            updateWebsiteByEvent({
              ...wu,
              type: 'text',
              content: {
                ...content,
                ops: newContentOps,
              },
            });

            setEditQuillSearchParamMentionModalOpen(false);
          }}
        />
      </div>
    </FullScreenModal>
  );
}
